/**
 * @file Helper for surface share links
 */

import { encodeString } from '@@/bits/base62'
import { __ } from '@@/bits/intl'
import { buildUrlFromPath } from '@@/bits/location'
import type {
    AuthorBreakoutLinkConfig,
    BreakoutLinkConfig,
    ColorBreakoutLinkConfig,
    PostColorId,
    SectionBreakoutLinkConfig,
    SingleSelectBreakoutLinkConfig
} from '@@/types'
import type { FetchOptionQueryHash } from '@padlet/fetch/src/types'

// #region generic breakout
function getBreakoutDisabledUrl(): string {
  return buildUrlFromPath('/breakout-disabled')
}
// #endregion generic breakout

// #region section breakout
function isSectionBreakoutLink(link: BreakoutLinkConfig): link is SectionBreakoutLinkConfig {
  return link.type === 'section_breakout'
}

function getSectionBreakoutPath(wallNamespace: string, sectionBreakoutIdentifier: string): string {
  return `/${wallNamespace}/breakout-room/${sectionBreakoutIdentifier}`
}

function getSectionBreakoutUrl(wallNamespace: string, sectionBreakoutIdentifier: string): string {
  const sectionBreakoutPath = getSectionBreakoutPath(wallNamespace, sectionBreakoutIdentifier)
  return buildUrlFromPath(sectionBreakoutPath)
}

function getSectionBreakoutPostPath(wallNamespace: string, sectionBreakoutIdentifier: string, postId: string): string {
  return getSectionBreakoutPath(wallNamespace, sectionBreakoutIdentifier) + `/wish/${postId}`
}

function getSectionBreakoutPostUrl(wallNamespace: string, sectionBreakoutIdentifier: string, postId: string): string {
  return getSectionBreakoutUrl(wallNamespace, sectionBreakoutIdentifier) + `/wish/${postId}`
}

// #endregion section breakout

// #region color breakout
function isColorBreakoutLink(link: BreakoutLinkConfig): link is ColorBreakoutLinkConfig {
  return link.type === 'color_breakout'
}

function getColorBreakoutIdentifier(color: PostColorId, wallHashid: string): string {
  return `color:${color}:${wallHashid}` // matches identifier in WallShareLinks::ColorBreakout
}

function getColorBreakoutPath(wallNamespace: string, color: PostColorId, wallHashid: string): string {
  const identifier = getColorBreakoutIdentifier(color, wallHashid)
  const encodedIdentifier = encodeString(identifier)
  return `/${wallNamespace}/breakout-room/${encodedIdentifier}`
}

function getColorBreakoutUrl(wallNamespace: string, color: PostColorId, wallHashid: string): string {
  return buildUrlFromPath(getColorBreakoutPath(wallNamespace, color, wallHashid))
}
// #endregion color breakout

// #region author breakout
function isAuthorBreakoutLink(link: BreakoutLinkConfig): link is AuthorBreakoutLinkConfig {
  return link.type === 'author_breakout'
}

function getAuthorBreakoutIdentifier(wallHashid: string): string {
  return `author:${wallHashid}`
}

function getAuthorBreakoutPath(wallNamespace: string, wallHashid: string): string {
  const identifier = getAuthorBreakoutIdentifier(wallHashid)
  const encodedIdentifier = encodeString(identifier)
  return `/${wallNamespace}/breakout-room/${encodedIdentifier}`
}

function getAuthorBreakoutUrl(wallNamespace: string, wallHashid: string): string {
  return buildUrlFromPath(getAuthorBreakoutPath(wallNamespace, wallHashid))
}
// #endregion author breakout

// #region single select property breakout
function isSingleSelectBreakoutLink(link: BreakoutLinkConfig): link is SingleSelectBreakoutLinkConfig {
  return link.type === 'single_select_breakout'
}

function getSingleSelectBreakoutIdentifier(customPropId: string, optionId: string, wallHashid: string): string {
  return `custom_prop:${customPropId}:${optionId}:${wallHashid}` // matches identifier in WallShareLinks::SingleSelectBreakout
}

function getSingleSelectBreakoutPath(
  wallNamespace: string,
  customPropId: string,
  optionId: string,
  wallHashid: string,
): string {
  const identifier = getSingleSelectBreakoutIdentifier(customPropId, optionId, wallHashid)
  const encodedIdentifier = encodeString(identifier)
  return `/${wallNamespace}/breakout-room/${encodedIdentifier}`
}

function getSingleSelectBreakoutUrl(
  wallNamespace: string,
  customPropId: string,
  optionId: string,
  wallHashid: string,
): string {
  return buildUrlFromPath(getSingleSelectBreakoutPath(wallNamespace, customPropId, optionId, wallHashid))
}
// #endregion

// #region submission request
const DEFAULT_SUBMISSION_REQUEST_CONFIRMATION_TEXT = __('Thank you for your contribution!')
function getSubmissionRequestDisabledUrl(): string {
  return buildUrlFromPath(`/submission-request-disabled`)
}

function getSubmissionRequestConfirmationUrl(submissionRequestLink: string): string {
  return submissionRequestLink + '/confirmation'
}

function isNavigatedFromSubmissionRequest(): boolean {
  return document.referrer?.includes('/submission-request/')
}

/* Submission request token */
let submissionRequestToken = ''

const getSubmissionRequestTokenHash = (): FetchOptionQueryHash => {
  if (submissionRequestToken === '') {
    // meta tag injected into doodle.html.erb
    // Used to skip auth for requests made in Submission Request view as permissions for Submission Request differ from the wall
    const submissionRequestTags = document.getElementsByName('submission-request-token')
    if (submissionRequestTags.length > 0) {
      const firstSubmissionRequestTag = submissionRequestTags[0] as HTMLMetaElement
      submissionRequestToken = firstSubmissionRequestTag.content
    }
  }

  return submissionRequestToken.length === 0 ? {} : { submission_request_token: submissionRequestToken }
}
export {
    getBreakoutDisabledUrl,
    isSectionBreakoutLink,
    getSectionBreakoutPath,
    getSectionBreakoutUrl,
    getSectionBreakoutPostPath,
    getSectionBreakoutPostUrl,
    isColorBreakoutLink,
    getColorBreakoutPath,
    getColorBreakoutUrl,
    isAuthorBreakoutLink,
    getAuthorBreakoutPath,
    getAuthorBreakoutUrl,
    isSingleSelectBreakoutLink,
    getSingleSelectBreakoutPath,
    getSingleSelectBreakoutUrl,
    DEFAULT_SUBMISSION_REQUEST_CONFIRMATION_TEXT,
    getSubmissionRequestDisabledUrl,
    getSubmissionRequestConfirmationUrl,
    isNavigatedFromSubmissionRequest,
    getSubmissionRequestTokenHash,
}

// #endregion submission request
